import { useState, useEffect, useCallback, useMemo } from "react";
import { useUrlState } from "@/lib/useUrlState";
import SearchBar from "@/components/SearchBar";
import Segmented from "@/components/Segmented";
import { Pill } from "@/components/FilterPills";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
} from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from "@/components/ui/tooltip";
import { Trash2, ArrowUpDown, ArrowUp, ArrowDown, RotateCcw, Archive, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import PersonPick from "@/components/PersonPick";
import PersonChip from "@/estimating/components/PersonChip";

const STATUS_OPTIONS = ["Pending", "In Review", "Complete"];

const STATUS_COLORS = {
  Pending: "bg-amber-100 text-amber-700 border-amber-200 hover:bg-amber-200",
  "In Review": "bg-blue-100 text-blue-700 border-blue-200 hover:bg-blue-200",
  Complete: "bg-emerald-100 text-emerald-700 border-emerald-200 hover:bg-emerald-200",
};

function SortHeader({ column, label }) {
  const sorted = column.getIsSorted();
  return (
    <button className="flex items-center gap-1.5 group" onClick={() => column.toggleSorting(sorted === "asc")} data-testid={`reconcile-sort-${column.id}`}>
      <span>{label}</span>
      <span className="opacity-0 group-hover:opacity-100 transition-opacity">
        {sorted === "asc" ? <ArrowUp className="h-3 w-3" /> : sorted === "desc" ? <ArrowDown className="h-3 w-3" /> : <ArrowUpDown className="h-3 w-3 text-slate-400" />}
      </span>
    </button>
  );
}

export default function ReconcilePage() {
  const [view, setView] = useUrlState("view", "active");   // view pills (UI_PATTERNS.md §4)
  const [activeItems, setActiveItems] = useState([]);
  const [archivedItems, setArchivedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [archivedSearchQuery, setArchivedSearchQuery] = useState("");
  const [amFilter, setAmFilter] = useState("ALL");
  const [editingCell, setEditingCell] = useState(null);
  const [sorting, setSorting] = useState([]);
  const [archivedSorting, setArchivedSorting] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const [active, archived] = await Promise.all([
        api.getReconcile(false),
        api.getReconcile(true),
      ]);
      setActiveItems(active);
      setArchivedItems(archived);
    } catch (e) {
      console.error("Failed to fetch reconcile data:", e);
      toast.error("Failed to load reconcile data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Get unique AMs from all items
  const uniqueAMs = useMemo(() => {
    const allItems = [...activeItems, ...archivedItems];
    const ams = new Set(allItems.map(item => item.account_manager).filter(Boolean));
    return Array.from(ams).sort();
  }, [activeItems, archivedItems]);

  const filteredActiveItems = useMemo(() => {
    let result = activeItems;
    if (amFilter !== "ALL") {
      result = result.filter(item => item.account_manager === amFilter);
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(item => 
        (item.project_name || "").toLowerCase().includes(q) ||
        (item.account_manager || "").toLowerCase().includes(q) ||
        (item.job_number || "").toLowerCase().includes(q) ||
        (item.pm || "").toLowerCase().includes(q)
      );
    }
    return result;
  }, [activeItems, amFilter, searchQuery]);

  const filteredArchivedItems = useMemo(() => {
    const byAm = amFilter === "ALL" ? archivedItems : archivedItems.filter((item) => item.account_manager === amFilter);
    if (!archivedSearchQuery) return byAm;
    const q = archivedSearchQuery.toLowerCase();
    return byAm.filter(item => 
      (item.project_name || "").toLowerCase().includes(q) ||
      (item.account_manager || "").toLowerCase().includes(q) ||
      (item.job_number || "").toLowerCase().includes(q) ||
      (item.pm || "").toLowerCase().includes(q)
    );
  }, [archivedItems, archivedSearchQuery, amFilter]);

  const handleAddProject = async () => {
    try {
      const newItem = await api.createReconcile({
        project_name: "",
        account_manager: "",
        job_number: "",
        estimator: "",
        pm: "",
        status: "Pending",
        source_project_id: ""
      });
      setActiveItems(prev => [newItem, ...prev]);
      setEditingCell({ rowId: newItem.id, field: "project_name" });
      toast.success("Project added");
    } catch (e) {
      toast.error("Failed to add project");
    }
  };

  const handleUpdate = async (itemId, field, value) => {
    try {
      const updated = await api.updateReconcile(itemId, { [field]: value });
      
      // If status changed to Complete, move to archived
      if (field === "status" && value === "Complete") {
        setActiveItems(prev => prev.filter(item => item.id !== itemId));
        setArchivedItems(prev => [updated, ...prev]);
        toast.success("Moved to Archived");
      } else {
        setActiveItems(prev => prev.map(item => item.id === itemId ? updated : item));
        setArchivedItems(prev => prev.map(item => item.id === itemId ? updated : item));
      }
      setEditingCell(null);
    } catch (e) {
      toast.error("Failed to update");
    }
  };

  const handleUnarchive = async (itemId) => {
    try {
      const updated = await api.unarchiveReconcile(itemId);
      setArchivedItems(prev => prev.filter(item => item.id !== itemId));
      setActiveItems(prev => [updated, ...prev]);
      toast.success("Moved back to Active with Pending status");
    } catch (e) {
      toast.error("Failed to unarchive");
    }
  };

  const handleDelete = async (itemId, isArchived = false) => {
    try {
      await api.deleteReconcile(itemId);
      if (isArchived) {
        setArchivedItems(prev => prev.filter(item => item.id !== itemId));
      } else {
        setActiveItems(prev => prev.filter(item => item.id !== itemId));
      }
      toast.success("Entry deleted");
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  // Active columns
  const activeColumns = useMemo(() => [
    {
      accessorKey: "account_manager",
      header: ({ column }) => <SortHeader column={column} label="AM" />,
      cell: ({ row }) => {
        const item = row.original;
        return <PersonPick value={item.account_manager} kind="am" testid={`reconcile-am-cell-${row.index}`}
                           onChange={(v) => handleUpdate(item.id, "account_manager", v)} />;
      
      },
      size: 140,
    },
    {
      accessorKey: "project_name",
      header: ({ column }) => <SortHeader column={column} label="Name" />,
      cell: ({ row }) => {
        const item = row.original;
        const isEditing = editingCell?.rowId === item.id && editingCell?.field === "project_name";
        if (isEditing) {
          return (
            <Input 
              defaultValue={item.project_name || ""} 
              autoFocus 
              className="h-7 text-sm w-full" 
              data-testid={`reconcile-name-input-${row.index}`}
              onBlur={(e) => handleUpdate(item.id, "project_name", e.target.value)}
              onKeyDown={(e) => { 
                if (e.key === "Enter") handleUpdate(item.id, "project_name", e.target.value); 
                if (e.key === "Escape") setEditingCell(null); 
              }}
            />
          );
        }
        return (
          <div 
            className="font-medium text-slate-900 cursor-pointer hover:text-[#007BFF] hover:bg-blue-50/50 rounded px-1 py-0.5 -mx-1 transition-colors"
            onClick={() => setEditingCell({ rowId: item.id, field: "project_name" })} 
            data-testid={`reconcile-cell-name-${row.index}`}
          >
            {item.project_name || <span className="text-slate-400 italic">Enter project name</span>}
          </div>
        );
      },
      size: 250,
    },
    {
      accessorKey: "job_number",
      header: ({ column }) => <SortHeader column={column} label="Job #" />,
      cell: ({ row }) => {
        const item = row.original;
        const isEditing = editingCell?.rowId === item.id && editingCell?.field === "job_number";
        if (isEditing) {
          return (
            <Input 
              defaultValue={item.job_number || ""} 
              autoFocus 
              maxLength={8}
              className="h-7 text-sm w-24 font-mono" 
              data-testid={`reconcile-job-input-${row.index}`}
              onBlur={(e) => handleUpdate(item.id, "job_number", e.target.value)}
              onKeyDown={(e) => { 
                if (e.key === "Enter") handleUpdate(item.id, "job_number", e.target.value); 
                if (e.key === "Escape") setEditingCell(null); 
              }}
            />
          );
        }
        return (
          <div 
            className="font-mono text-sm text-slate-700 cursor-pointer hover:text-[#007BFF] hover:bg-blue-50/50 rounded px-1 py-0.5 -mx-1 transition-colors"
            onClick={() => setEditingCell({ rowId: item.id, field: "job_number" })} 
            data-testid={`reconcile-cell-job-${row.index}`}
          >
            {item.job_number || <span className="text-slate-400 italic">Enter job #</span>}
          </div>
        );
      },
      size: 100,
    },
    {
      accessorKey: "estimator",
      header: ({ column }) => <SortHeader column={column} label="Est" />,
      cell: ({ row }) => {
        const item = row.original;
        return <PersonPick value={item.estimator} kind="est" testid={`reconcile-estimator-cell-${row.index}`}
                           onChange={(v) => handleUpdate(item.id, "estimator", v)} />;
      
      },
      size: 130,
    },
    {
      accessorKey: "pm",
      header: ({ column }) => <SortHeader column={column} label="PM" />,
      cell: ({ row }) => {
        const item = row.original;
        const isEditing = editingCell?.rowId === item.id && editingCell?.field === "pm";
        if (isEditing) {
          return (
            <Input 
              defaultValue={item.pm || ""} 
              autoFocus 
              className="h-7 text-sm w-36" 
              data-testid={`reconcile-pm-input-${row.index}`}
              onBlur={(e) => handleUpdate(item.id, "pm", e.target.value)}
              onKeyDown={(e) => { 
                if (e.key === "Enter") handleUpdate(item.id, "pm", e.target.value); 
                if (e.key === "Escape") setEditingCell(null); 
              }}
            />
          );
        }
        return (
          <div 
            className="text-sm text-slate-700 cursor-pointer hover:text-[#007BFF] hover:bg-blue-50/50 rounded px-1 py-0.5 -mx-1 transition-colors"
            onClick={() => setEditingCell({ rowId: item.id, field: "pm" })} 
            data-testid={`reconcile-cell-pm-${row.index}`}
          >
            {item.pm || <span className="text-slate-400 italic">Enter PM name</span>}
          </div>
        );
      },
      size: 150,
    },
    {
      accessorKey: "status",
      header: ({ column }) => <SortHeader column={column} label="Status" />,
      cell: ({ row }) => {
        const item = row.original;
        return (
          <Select value={item.status} onValueChange={(v) => handleUpdate(item.id, "status", v)}>
            <SelectTrigger 
              className={cn("h-7 text-sm w-28 border font-medium", STATUS_COLORS[item.status])}
              data-testid={`reconcile-status-${row.index}`}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        );
      },
      size: 130,
    },
    {
      id: "actions",
      header: () => null,
      cell: ({ row }) => (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button 
                className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-400 hover:text-red-500 p-1 rounded"
                onClick={() => handleDelete(row.original.id)} 
                data-testid={`delete-reconcile-${row.index}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent>Delete entry</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ),
      size: 50,
    },
  ], [editingCell]);

  // Archived columns (similar but with unarchive button instead of status editing)
  const archivedColumns = useMemo(() => [
    {
      accessorKey: "account_manager",
      header: ({ column }) => <SortHeader column={column} label="AM" />,
      cell: ({ row }) => (
        <div className="text-sm text-slate-700" data-testid={`archived-reconcile-cell-am-${row.index}`}>
          {row.original.account_manager ? <PersonChip name={row.original.account_manager} /> : <span className="text-slate-400">—</span>}
        </div>
      ),
      size: 140,
    },
    {
      accessorKey: "project_name",
      header: ({ column }) => <SortHeader column={column} label="Name" />,
      cell: ({ row }) => (
        <div className="font-medium text-slate-900" data-testid={`archived-reconcile-cell-name-${row.index}`}>
          {row.original.project_name || <span className="text-slate-400">—</span>}
        </div>
      ),
      size: 250,
    },
    {
      accessorKey: "job_number",
      header: ({ column }) => <SortHeader column={column} label="Job #" />,
      cell: ({ row }) => (
        <div className="font-mono text-sm text-slate-700" data-testid={`archived-reconcile-cell-job-${row.index}`}>
          {row.original.job_number || <span className="text-slate-400">—</span>}
        </div>
      ),
      size: 100,
    },
    {
      accessorKey: "estimator",
      header: ({ column }) => <SortHeader column={column} label="Est" />,
      cell: ({ row }) => (
        <div className="text-sm text-slate-600" data-testid={`archived-reconcile-cell-estimator-${row.index}`}>
          {row.original.estimator ? <PersonChip name={row.original.estimator} /> : <span className="text-slate-400">—</span>}
        </div>
      ),
      size: 130,
    },
    {
      accessorKey: "pm",
      header: ({ column }) => <SortHeader column={column} label="PM" />,
      cell: ({ row }) => (
        <div className="text-sm text-slate-700" data-testid={`archived-reconcile-cell-pm-${row.index}`}>
          {row.original.pm || <span className="text-slate-400">—</span>}
        </div>
      ),
      size: 150,
    },
    {
      accessorKey: "status",
      header: ({ column }) => <SortHeader column={column} label="Status" />,
      cell: ({ row }) => (
        <Badge variant="outline" className={cn("font-medium", STATUS_COLORS[row.original.status])} data-testid={`archived-reconcile-cell-status-${row.index}`}>
          {row.original.status}
        </Badge>
      ),
      size: 120,
    },
    {
      id: "actions",
      header: () => null,
      cell: ({ row }) => (
        <TooltipProvider>
          <div className="flex items-center gap-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <button 
                  className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-400 hover:text-emerald-600 p-1 rounded"
                  onClick={() => handleUnarchive(row.original.id)} 
                  data-testid={`unarchive-reconcile-${row.index}`}
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent>Move back to Active (Pending)</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <button 
                  className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-400 hover:text-red-500 p-1 rounded"
                  onClick={() => handleDelete(row.original.id, true)} 
                  data-testid={`delete-archived-reconcile-${row.index}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent>Delete entry</TooltipContent>
            </Tooltip>
          </div>
        </TooltipProvider>
      ),
      size: 70,
    },
  ], []);

  const activeTable = useReactTable({
    data: filteredActiveItems,
    columns: activeColumns,
    state: { sorting, globalFilter: searchQuery },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  const archivedTable = useReactTable({
    data: filteredArchivedItems,
    columns: archivedColumns,
    state: { sorting: archivedSorting, globalFilter: archivedSearchQuery },
    onSortingChange: setArchivedSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center" data-testid="reconcile-loading">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 border-2 border-[#007BFF] border-t-transparent rounded-full animate-spin" />
          <span className="text-sm text-slate-500">Loading reconcile data...</span>
        </div>
      </div>
    );
  }

  const archived = view === "archived";
  return (
    <div className="page-pane gap-3" data-testid="reconcile-page">
      <div className="flex shrink-0 flex-wrap items-center gap-2" data-testid="reconcile-toolbar">
        {archived
          ? <SearchBar value={archivedSearchQuery} onChange={setArchivedSearchQuery} placeholder="Search..." testId="reconcile-archived-search" />
          : <SearchBar value={searchQuery} onChange={setSearchQuery} placeholder="Search..." testId="reconcile-search" />}
        <Segmented
          options={[{ key: "active", label: "Active", testId: "reconcile-view-active" },
                    { key: "archived", label: "Archived", count: archivedItems.length || undefined, testId: "reconcile-view-archived" }]}
          value={view} onChange={setView}
        >
          <span className="mx-0.5 h-4 w-px bg-slate-300" />
          <Pill p={{ key: "reconcile-am", label: "AM", options: uniqueAMs.map((a) => ({ value: a, label: a })),
                     value: amFilter === "ALL" ? "" : amFilter, onChange: (v) => setAmFilter(v || "ALL") }} />
        </Segmented>
        {!archived && (
          <Button size="sm" className="h-7 gap-1 px-2.5 text-xs" onClick={handleAddProject} data-testid="add-reconcile-btn">
            <Plus className="h-3.5 w-3.5" /> Project
          </Button>)}
        <span className="ml-auto text-xs text-slate-400 font-medium" data-testid="reconcile-count">
          {archived ? filteredArchivedItems.length : filteredActiveItems.length} project{(archived ? filteredArchivedItems.length : filteredActiveItems.length) !== 1 ? "s" : ""}
        </span>
      </div>
      <div className="pane-card border border-slate-200 bg-white">
        <div className="flex-1 overflow-auto bg-white">
          {archived ? (
            <Table>
              <TableHeader className="sticky top-0 bg-white/95 backdrop-blur-sm z-10">
                {archivedTable.getHeaderGroups().map(hg => (
                  <TableRow key={hg.id} className="border-b-2 border-slate-200 hover:bg-transparent">
                    {hg.headers.map(header => (
                      <TableHead 
                        key={header.id} 
                        className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 px-4 py-3 whitespace-nowrap"
                        style={{ width: header.getSize() }} 
                        data-testid={`reconcile-archived-col-${header.id}`}
                      >
                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {archivedTable.getRowModel().rows.length > 0 ? archivedTable.getRowModel().rows.map(row => (
                  <TableRow 
                    key={row.id} 
                    className="group hover:bg-slate-50/80 transition-colors border-b border-slate-100" 
                    data-testid={`reconcile-archived-row-${row.index}`}
                  >
                    {row.getVisibleCells().map(cell => (
                      <TableCell key={cell.id} className="px-4 py-2.5" style={{ width: cell.column.getSize() }}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                )) : (
                  <TableRow>
                    <TableCell colSpan={archivedColumns.length} className="h-48 text-center">
                      <div className="flex flex-col items-center gap-3" data-testid="reconcile-archived-empty">
                        <div className="h-14 w-14 rounded-xl bg-slate-100 flex items-center justify-center">
                          <Archive className="h-7 w-7 text-slate-400" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-600">No archived items</p>
                          <p className="text-xs text-slate-400 mt-1">Projects with "Complete" status will appear here</p>
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          ) : (
            <Table>
              <TableHeader className="sticky top-0 bg-white/95 backdrop-blur-sm z-10">
                {activeTable.getHeaderGroups().map(hg => (
                  <TableRow key={hg.id} className="border-b-2 border-slate-200 hover:bg-transparent">
                    {hg.headers.map(header => (
                      <TableHead 
                        key={header.id} 
                        className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 px-4 py-3 whitespace-nowrap"
                        style={{ width: header.getSize() }} 
                        data-testid={`reconcile-col-${header.id}`}
                      >
                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {activeTable.getRowModel().rows.length > 0 ? activeTable.getRowModel().rows.map(row => (
                  <TableRow 
                    key={row.id} 
                    className="group hover:bg-slate-50/80 transition-colors border-b border-slate-100" 
                    data-testid={`reconcile-row-${row.index}`}
                  >
                    {row.getVisibleCells().map(cell => (
                      <TableCell key={cell.id} className="px-4 py-2.5" style={{ width: cell.column.getSize() }}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                )) : (
                  <TableRow>
                    <TableCell colSpan={activeColumns.length} className="h-48 text-center">
                      <div className="flex flex-col items-center gap-3" data-testid="reconcile-empty">
                        <div className="h-14 w-14 rounded-xl bg-slate-100 flex items-center justify-center">
                          <Archive className="h-7 w-7 text-slate-400" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-600">No active reconcile items</p>
                          <p className="text-xs text-slate-400 mt-1">Projects will appear here when their status changes to "Won" in the Pipeline</p>
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
    </div>
  );
}
